import * as React from 'react';
import { useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { nullSafeGet } from '../../../utils/DataAccessUtils';
import {
	appendBidderNotes,
	appendBidderNotesBulk,
	fetchAllBidderNotes,
} from '../../../thunks/bidders_thunks';
import { WEB_SOCKET_ENTITY_TYPES } from '../../../utils/WebSocketUtils';
import CommonNotesChatPage from '../../common_notes_chat_page/CommonNotesChatPage';
import CommonNoteInputForm from '../../common_note_input_form/CommonNoteInputForm';
import { owuploadCustomRequest } from '../../../thunks/file_thunks';

require('./BidderNotes.less');

const getMessageChannelId = (selectedBidder) =>
	`${selectedBidder.requestForProposalId}_${selectedBidder.supplierFacilityId}`;

const WS_CONFIG = {
	entityIdName: 'bidderId',
	entityType: WEB_SOCKET_ENTITY_TYPES.BIDDER_NOTES,
	newNoteEntityType: WEB_SOCKET_ENTITY_TYPES.BIDDER_NOTES,
};

const BidderNotesPage = ({
	selectedBidder,
	currentUser,
	userType,
	messages,
	appendNote: appendNoteAction,
	appendNotesBulk: appendNotesBulkAction,
	fetchMessages,
}) => {
	const entityId = getMessageChannelId(selectedBidder);

	const fetchNotes = useCallback(
		() => fetchMessages(selectedBidder),
		[fetchMessages, selectedBidder]
	);

	const appendNote = useCallback(
		(note: any) => {
			return appendNoteAction(selectedBidder, {
				note,
				rfpId: nullSafeGet('requestForProposalId', selectedBidder),
			});
		},
		[appendNoteAction, selectedBidder]
	);

	// Same body as appendNote, one request for the whole photo batch.
	const appendNotesBulk = useCallback(
		(notes: any[]) => {
			return appendNotesBulkAction(selectedBidder, {
				notes,
				rfpId: nullSafeGet('requestForProposalId', selectedBidder),
			});
		},
		[appendNotesBulkAction, selectedBidder]
	);

	// Pass a minimal workOrder shape for audio upload (needs workOrderId)
	const workOrderShape = useMemo(
		() => ({ id: nullSafeGet('workOrderId', selectedBidder) }),
		[selectedBidder]
	);

	const renderInputForm = useCallback(
		({ handleTyping, onSendNote, typingDetails, newNoteCount, onRefresh }) => (
			<CommonNoteInputForm
				appendNote={appendNote}
				appendNotesBulk={appendNotesBulk}
				currentUser={currentUser}
				workOrder={workOrderShape}
				onTyping={handleTyping}
				onSendNote={onSendNote}
				typingDetails={typingDetails}
				newNoteCount={newNoteCount}
				onRefresh={onRefresh}
				customUploadRequest={owuploadCustomRequest}
			/>
		),
		[appendNote, appendNotesBulk, currentUser, workOrderShape]
	);

	return (
		<div className="BidderNotes">
			<CommonNotesChatPage
				entityId={entityId}
				fetchNotes={fetchNotes}
				userType={userType}
				heightSelector="rfp-detail-header"
				heightExtraSelectors={['bidder-chat-header']}
				wsConfig={WS_CONFIG}
				currentUser={currentUser}
				messages={messages}
				renderInputForm={renderInputForm}
				inlineInput
			/>
		</div>
	);
};

const mapStateToProps = (state, ownProps) => ({
	userType: state.session.userType,
	isAuthenticated: state.session.isAuthenticated,
	messages: state.messages,
	currentUser: state.session.currentUser,
	errors: state.session.errors,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	appendNote: (bidder, note) => dispatch(appendBidderNotes(ownProps.userType)(bidder, note)),
	appendNotesBulk: (bidder, notes) =>
		dispatch(appendBidderNotesBulk(ownProps.userType)(bidder, notes)),
	fetchMessages: (bidder) => dispatch(fetchAllBidderNotes(ownProps.userType)(bidder)),
});

const ComponentWithoutUserType = withRouter(
	connect(mapStateToProps, mapDispatchToProps)(BidderNotesPage as any)
);

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType as any);
